/**
 * CORE-881 — как отчёт о продажах превращается в то, что читает мастер.
 *
 * Модуль без зависимостей от React Native: здесь решения, и их нужно проверять
 * тестами. Главное из них одно и повторяется всюду — **`null` не ноль**. Пока
 * комиссия не утверждена (CORE-13), сервер присылает `null`, и показать вместо
 * него «0 ₽» значит сказать мастеру, что он получает всё. Прочерк читается так
 * же. Поэтому нерассчитанное называется словами.
 *
 * Ни одна сумма здесь не вычисляется. Gross, комиссия, возвраты и начисление
 * считаются на сервере — фронт их только форматирует (критерий приёмки №2).
 */
import type {
  FinancePeriod,
  MasterSalesTerms,
  PayoutStatus,
  ProgramSalesRow,
  SalesOperation,
} from '../../types/masterSales';

const CURRENCY_SYMBOLS: Record<string, string> = {
  RUB: '₽',
  USD: '$',
  EUR: '€',
};

/**
 * Сумма с валютой.
 *
 * Незнакомый код валюты печатается как есть, а не проглатывается: увидеть
 * «1500 KZT» полезнее, чем «1500».
 */
export function formatMoney(amount: number, currency: string): string {
  const value = new Intl.NumberFormat('ru-RU').format(amount);
  const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
  return symbol === '' ? value : `${value} ${symbol}`;
}

/**
 * Расчётная сумма, которой может не быть.
 *
 * Возвращает `null`, когда показывать нечего, — вызывающий обязан подставить
 * объяснение словами, а не прочерк. Ради этого функция и отделена от
 * {@link formatMoney}: перепутать их местами нельзя.
 */
export function formatAccrual(amount: number | null, currency: string): string | null {
  return amount == null ? null : formatMoney(amount, currency);
}

/**
 * Почему расчётных сумм нет.
 *
 * Текст один на весь экран и говорит правду: дело не в отсутствии продаж, а в
 * неутверждённых условиях. Мастер, увидевший «—» напротив «начислено», решил бы,
 * что не заработал ничего.
 */
export function termsExplanation(terms: MasterSalesTerms): string {
  if (terms.configured) return '';
  return terms.reason === 'COMMISSION_NOT_CONFIGURED'
    ? 'Комиссия платформы пока не утверждена, поэтому начисления не рассчитаны. Продажи и возвраты показаны полностью.'
    : 'Условия расчёта пока не утверждены, поэтому начисления не рассчитаны.';
}

/**
 * Условия расчёта человеческим языком.
 *
 * Проценты не зашиты в код: ставка приходит с сервера в базисных пунктах и
 * только переводится в проценты (критерий приёмки №6).
 */
export function termsSummary(terms: MasterSalesTerms): string {
  if (!terms.configured) return '';

  const parts: string[] = [];
  if (terms.commissionRateBps != null) {
    parts.push(`комиссия ${formatBps(terms.commissionRateBps)}`);
  }
  if (terms.holdDays != null) {
    parts.push(`выплата через ${terms.holdDays} ${pluralDays(terms.holdDays)}`);
  }
  const period = payoutPeriodLabel(terms.payoutPeriod);
  if (period !== '') parts.push(period);

  return parts.join(' · ');
}

/** Базисные пункты в проценты: 3000 → «30%», 250 → «2,5%». */
export function formatBps(bps: number): string {
  const percent = bps / 100;
  const text = Number.isInteger(percent) ? String(percent) : percent.toFixed(2).replace(/0+$/, '');
  return `${text.replace('.', ',')}%`;
}

function payoutPeriodLabel(period: MasterSalesTerms['payoutPeriod']): string {
  switch (period) {
    case 'monthly': return 'выплаты раз в месяц';
    case 'biweekly': return 'выплаты раз в две недели';
    case 'onRequest': return 'выплата по запросу';
    default: return '';
  }
}

function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}

/**
 * Подпись состояния выплат.
 *
 * «Выплачено» среди них нет намеренно: реестра выплат ещё не существует, и
 * утверждать, что деньги ушли, было бы неправдой.
 */
export function payoutStatusLabel(status: PayoutStatus): string {
  switch (status) {
    case 'available': return 'Доступно к выплате';
    case 'hold': return 'Ожидает выплаты';
    case 'nothing': return 'Начислений за период нет';
    default: return 'Расчёт не настроен';
  }
}

/** Подпись периода для фильтра и заголовка. */
export function periodLabel(period: FinancePeriod): string {
  switch (period) {
    case '7d': return '7 дней';
    case '30d': return '30 дней';
    default: return 'Всё время';
  }
}

/**
 * Строка операции: знак у суммы.
 *
 * В данных возврат — величина, а не отрицательное число. Знак ставит экран, и
 * это правильно: минус перед суммой — оформление, а не факт.
 */
export function operationAmount(op: SalesOperation): string {
  if (op.status === 'refunded') {
    const value = op.refund ?? op.amount;
    return `−${formatMoney(value, op.currency)}`;
  }
  return formatMoney(op.amount, op.currency);
}

export function operationStatusLabel(op: SalesOperation): string {
  return op.status === 'refunded' ? 'Возврат' : 'Продажа';
}

/**
 * Стоит ли показывать строку возвратов у программы.
 *
 * Нулевые возвраты — не новость, и строка «возвраты: 0» только удлиняет
 * карточку. Появляется, когда есть что показать.
 */
export function hasRefunds(row: { refundedCount: number }): boolean {
  return row.refundedCount > 0;
}

/**
 * Компактная строка для карточки в «Созданном».
 *
 * Ровно два числа: сколько купили и на какую сумму. Карточка черновика не
 * финансовый отчёт — комиссии и hold'у там не место.
 */
export function compactSalesLine(
  sales: { paidCount: number; gross: number | null; currency: string | null; mixedCurrency: boolean } | null | undefined,
): string {
  if (!sales || sales.paidCount === 0) return '';

  const count = `${sales.paidCount} ${pluralPurchases(sales.paidCount)}`;
  if (sales.mixedCurrency || sales.gross == null || sales.currency == null) {
    // Разные валюты одной суммой не показать, а придумывать курс нельзя.
    return count;
  }
  return `${count} · ${formatMoney(sales.gross, sales.currency)}`;
}

function pluralPurchases(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'покупка';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'покупки';
  return 'покупок';
}

/**
 * Есть ли в отчёте хоть что-то за период.
 *
 * Пустой период — обычное состояние нового мастера, и говорить о нём надо
 * спокойно, а не показывать таблицу нулей.
 */
export function isEmptyReport(items: ProgramSalesRow[]): boolean {
  return items.length === 0;
}
